import { useMemo, useState } from 'react';
import { useStormReports, type StormReport } from '@/hooks/useStormReports';
import { StormMapView } from './StormMapView';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CloudHail, Wind, Tornado, RefreshCw, AlertTriangle, MapPin } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';


export function LiveStormMap() {
  const [days, setDays] = useState(3);
  const { reports, stats, loading, refetch } = useStormReports({ days });
  const [selected, setSelected] = useState<StormReport | null>(null);

  const sortedReports = useMemo(
    () => [...reports].sort((a, b) => (a.date < b.date ? 1 : -1) || (a.time < b.time ? 1 : -1)),
    [reports],
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
      <div className="space-y-4">
        {/* Stat tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatTile icon={<CloudHail className="w-4 h-4" />} label="Hail Reports" value={stats?.hail ?? 0} accent="bg-orange-500/10 border-orange-500/30 text-orange-600 dark:text-orange-400" sub={stats?.maxHail ? `Max ${stats.maxHail.toFixed(2)}"` : undefined} />
          <StatTile icon={<Wind className="w-4 h-4" />} label="Wind Reports" value={stats?.wind ?? 0} accent="bg-yellow-500/10 border-yellow-500/30 text-yellow-700 dark:text-yellow-400" sub={stats?.maxWind ? `Max ${stats.maxWind} mph` : undefined} />
          <StatTile icon={<Tornado className="w-4 h-4" />} label="Tornadoes" value={stats?.tornado ?? 0} accent="bg-purple-500/10 border-purple-500/30 text-purple-600 dark:text-purple-400" />
          <StatTile icon={<AlertTriangle className="w-4 h-4" />} label="Total Events" value={stats?.total ?? 0} accent="bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400" />
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 24 hours</SelectItem>
              <SelectItem value="3">Last 3 days</SelectItem>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refetch} disabled={loading} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <span className="text-xs text-muted-foreground ml-auto">Source: NOAA Storm Prediction Center</span>
        </div>

        <StormMapView reports={reports} onSelectReport={setSelected} height="600px" />
      </div>

      {/* Side feed */}
      <Card className="h-[840px]">
        <CardContent className="p-0 h-full flex flex-col">
          <div className="px-4 py-3 border-b border-border">
            <div className="font-semibold text-sm">Recent Reports</div>
            <div className="text-xs text-muted-foreground">{reports.length} events · click to view on map</div>
          </div>
          <ScrollArea className="flex-1">
            <div className="divide-y divide-border">
              {sortedReports.length === 0 && !loading && (
                <div className="p-6 text-center text-sm text-muted-foreground">No storm reports in this window</div>
              )}
              {sortedReports.map((r, i) => (
                <button
                  key={`${r.date}-${r.time}-${i}`}
                  onClick={() => setSelected(r)}
                  className={`w-full text-left p-3 hover:bg-muted/50 transition ${selected === r ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <ReportBadge report={r} />
                    <span className="text-xs text-muted-foreground">{r.date}</span>
                  </div>
                  <div className="text-sm font-medium text-foreground line-clamp-1">{r.location || 'Unknown'}, {r.state}</div>
                  <div className="text-xs text-muted-foreground line-clamp-1">{r.county} County · {r.time} UTC</div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Detail dialog inline */}
      {selected && (
        <div className="lg:col-span-2">
          <Card className="border-primary/30">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ReportBadge report={selected} />
                  <span className="font-semibold text-foreground">{selected.location}, {selected.state}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>Close</Button>
              </div>
              <div className="text-sm text-muted-foreground">{selected.county} County · {selected.date} @ {selected.time} UTC</div>
              <div className="text-sm text-foreground"><MapPin className="inline w-3.5 h-3.5 mr-1" /> {selected.lat.toFixed(4)}, {selected.lng.toFixed(4)}</div>
              {selected.comments && <div className="text-sm text-foreground italic">"{selected.comments}"</div>}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

function StatTile({ icon, label, value, sub, accent }: { icon: React.ReactNode; label: string; value: number; sub?: string; accent: string }) {
  return (
    <div className={`rounded-xl border p-3 ${accent}`}>
      <div className="flex items-center gap-2 text-xs font-medium opacity-80">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value.toLocaleString()}</div>
      {sub && <div className="text-xs opacity-70">{sub}</div>}
    </div>
  );
}

function ReportBadge({ report }: { report: StormReport }) {
  if (report.type === 'tornado') return <Badge className="bg-purple-600 text-white">Tornado EF{Math.round(report.magnitude)}</Badge>;
  if (report.type === 'wind') return <Badge className="bg-yellow-600 text-white">Wind {Math.round(report.magnitude)} mph</Badge>;
  return <Badge className="bg-orange-600 text-white">Hail {report.magnitude.toFixed(2)}"</Badge>;
}
